import { useLayoutEffect } from 'react'

/**
 * Reaching the television's one scroll region, without a mouse.
 *
 * On the television the shell is a fixed frame — header pinned at the top, key
 * legend pinned at the bottom, and exactly one box between them that scrolls.
 * That arrangement was tried once before and taken out again, and the note it
 * left behind is the thing this file exists to answer:
 *
 *   "the wheel only works over the right element"
 *
 * Which is true, and fatal, for a small box in the middle of a page. A wheel
 * scrolls whatever the pointer happens to be resting over, and nobody aims a
 * pointer at a television — it sits wherever it was left, very likely over the
 * header or the legend, where a wheel would do nothing at all and the screen
 * would look broken.
 *
 * So the wheel is taken at the window and forwarded to the region deliberately,
 * wherever the pointer is. The pointer stops mattering, which on a set with no
 * mouse is the only sane arrangement.
 *
 * The keys are the other half. The keyboard is the whole of the input on the
 * Pi, and a region only a wheel can reach is one broken wheel away from
 * content nobody can get to. Page Up and Page Down, Home and End are free on
 * every screen in this app — checked against all six games and the picker —
 * and they are what somebody would press.
 *
 * Deliberately **not** done by making the region focusable. `tabIndex={0}`
 * would let the browser scroll it natively and would also add a tab stop, and
 * there is a test asserting the board adds nothing to the tab order. The tab
 * order is a promise to somebody working through the screen one stop at a
 * time; a scroll box is not a stop.
 */
export function useFrameScroll(region: HTMLElement | null, enabled: boolean): void {
  // Before the paint, for the same reason `useGameKeys` is: a wheel or a
  // PageDown in the gap between the frame being drawn and a passive flush
  // would find nothing listening and simply be lost.
  useLayoutEffect(() => {
    if (!region || !enabled) return

    function scrollBy(amount: number): void {
      region?.scrollBy({ top: amount, behavior: 'auto' })
    }

    function onWheel(event: WheelEvent): void {
      if (!region) return

      /*
       * A wheel already over the region is the browser's to handle: forwarding
       * it as well would scroll twice as far as the wheel was turned.
       *
       * `composedPath` rather than walking `parentElement`, so this still holds
       * if any of it is ever moved inside a shadow root.
       */
      if (event.composedPath().includes(region)) return

      /*
       * A gesture with no vertical part is not ours. Forwarding it would move
       * the region nowhere and suppress the browser's default for a wheel we
       * did nothing with, which is the sort of thing that goes unnoticed until
       * something on the screen wants a sideways wheel.
       */
      if (event.deltaY === 0) return

      region.scrollBy({ top: event.deltaY, behavior: 'auto' })
      event.preventDefault()
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.metaKey || event.ctrlKey || event.altKey) return

      /*
       * A screen that has claimed the key wins.
       *
       * The frame is the fallback for a screen with nothing better to do with
       * Home and End, not an owner of them. On the picker they mean the first
       * and last game, and scrolling the region underneath that would leave
       * the ring pointing at one card while the screen showed another — which
       * is the thing the roving cursor exists to prevent.
       *
       * This works on ordering rather than luck: React runs a child's effects
       * before its parent's, so a screen inside the layout has already bound
       * its listener by the time this one binds, and runs first.
       */
      if (event.defaultPrevented) return

      // Somebody typing into a box gets Home and End as the browser's own,
      // which is what those keys mean inside a field.
      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement)
      ) {
        return
      }

      // A page is most of the region rather than all of it, so something stays
      // on screen across the jump and she can see where she got to.
      const page = region ? region.clientHeight * 0.8 : 0

      switch (event.key) {
        case 'PageDown':
          event.preventDefault()
          return scrollBy(page)
        case 'PageUp':
          event.preventDefault()
          return scrollBy(-page)
        case 'Home':
          event.preventDefault()
          return void region?.scrollTo({ top: 0, behavior: 'auto' })
        case 'End':
          event.preventDefault()
          return void region?.scrollTo({ top: region.scrollHeight, behavior: 'auto' })
        default:
      }
    }

    // Not passive: forwarding the wheel means preventing the default one, and
    // a passive listener is not allowed to.
    window.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [region, enabled])
}
